import { useRef, useState } from 'react';

/**
 * Графики нарисованы вручную на SVG. Готовая библиотека вроде Recharts
 * добавила бы к Mini App сотни килобайт ради двух простых форм, а грузится
 * приложение по мобильной сети.
 */

const W = 320;
const H = 132;
const PAD = { l: 26, r: 8, t: 10, b: 18 };
const PLOT_W = W - PAD.l - PAD.r;
const PLOT_H = H - PAD.t - PAD.b;

export interface Point {
  label: string;
  value: number;
  /** Что показать во всплывающей подсказке. */
  hint: string;
  /** Выделенный столбик — например, день из серии. */
  accent?: boolean;
}

interface HoverState {
  i: number;
  x: number;
  y: number;
}

function useHover(count: number) {
  const ref = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const svg = ref.current;
    if (!svg || count === 0) return;
    const box = svg.getBoundingClientRect();
    const xInView = ((e.clientX - box.left) / box.width) * W;
    const t = (xInView - PAD.l) / PLOT_W;
    const i = Math.max(0, Math.min(count - 1, Math.round(t * (count - 1))));
    setHover({ i, x: ((e.clientX - box.left) / box.width) * 100, y: 0 });
  };

  return { ref, hover, onMove, clear: () => setHover(null) };
}

function Tooltip({ left, children }: { left: number; children: React.ReactNode }) {
  // Прижимаем к краям, чтобы подсказка не уезжала за экран на первом и последнем дне.
  const clamped = Math.max(14, Math.min(86, left));
  return (
    <div className="tooltip" style={{ left: `${clamped}%`, top: 0 }}>
      {children}
    </div>
  );
}

/**
 * Линия: одна серия, поэтому легенда не нужна — её называет заголовок.
 * `min` поднимает низ шкалы: точность живёт в диапазоне 60-100%, и от нуля
 * линия прижималась бы к верхнему краю, скрывая ровно тот рост, ради которого
 * график и нужен. Нижняя граница всегда подписана на оси, так что это не обман.
 */
export function LineChart({ points, max = 1, min = 0, format }: {
  points: Point[];
  max?: number;
  min?: number;
  format: (v: number) => string;
}) {
  const { ref, hover, onMove, clear } = useHover(points.length);
  if (points.length < 2) return <div className="empty small">Данных пока мало — позанимайся ещё</div>;

  // Если данные проседают ниже предложенного низа, показываем всё от нуля.
  const lo = points.some((p) => p.value < min) ? 0 : min;
  const span = Math.max(1e-6, max - lo);

  const x = (i: number) => PAD.l + (i / (points.length - 1)) * PLOT_W;
  const y = (v: number) =>
    PAD.t + PLOT_H - ((Math.max(lo, Math.min(v, max)) - lo) / span) * PLOT_H;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${PAD.t + PLOT_H} L${x(0).toFixed(1)},${PAD.t + PLOT_H} Z`;
  const last = points[points.length - 1]!;

  return (
    <div className="chart-wrap">
      <svg
        ref={ref}
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        onPointerMove={onMove}
        onPointerLeave={clear}
      >
        {[0, 0.5, 1].map((f) => {
          const v = lo + span * f;
          return (
            <g key={f}>
              <line className="grid-line" x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} />
              <text className="axis" x={0} y={y(v) + 3}>{format(v)}</text>
            </g>
          );
        })}

        <path className="area" d={area} />
        <path className="line" d={line} />

        {/* Подписываем только последнюю точку: число над каждой превращает график в таблицу. */}
        <circle className="dot" cx={x(points.length - 1)} cy={y(last.value)} r={4} />

        {hover && (
          <>
            <line className="crosshair" x1={x(hover.i)} x2={x(hover.i)} y1={PAD.t} y2={PAD.t + PLOT_H} />
            <circle className="dot" cx={x(hover.i)} cy={y(points[hover.i]!.value)} r={4.5} />
          </>
        )}

        <text className="axis" x={PAD.l} y={H - 4}>{points[0]!.label}</text>
        <text className="axis" x={W - PAD.r} y={H - 4} textAnchor="end">{last.label}</text>
      </svg>

      {hover && <Tooltip left={hover.x}>{points[hover.i]!.hint}</Tooltip>}
    </div>
  );
}

/** Столбики: скруглённый торец сверху, 2px просвет между соседями. */
export function BarChart({ points, format }: { points: Point[]; format: (v: number) => string }) {
  const { ref, hover, onMove, clear } = useHover(points.length);
  if (points.length === 0) return <div className="empty small">Пока нет данных</div>;

  const max = Math.max(1, ...points.map((p) => p.value));
  const slot = PLOT_W / points.length;
  const barW = Math.max(2, slot - 2);
  const r = Math.min(4, barW / 2);

  return (
    <div className="chart-wrap">
      <svg
        ref={ref}
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        onPointerMove={onMove}
        onPointerLeave={clear}
      >
        {[0, 1].map((f) => (
          <g key={f}>
            <line
              className="grid-line"
              x1={PAD.l}
              x2={W - PAD.r}
              y1={PAD.t + PLOT_H - f * PLOT_H}
              y2={PAD.t + PLOT_H - f * PLOT_H}
            />
            <text className="axis" x={0} y={PAD.t + PLOT_H - f * PLOT_H + 3}>{format(max * f)}</text>
          </g>
        ))}

        {points.map((p, i) => {
          const h = (p.value / max) * PLOT_H;
          const bx = PAD.l + i * slot + 1;
          const by = PAD.t + PLOT_H - h;
          if (h <= 0) return null;
          return (
            <rect
              key={i}
              className={p.accent === false ? 'bar dim' : 'bar'}
              x={bx}
              y={by}
              width={barW}
              height={h}
              rx={Math.min(r, h / 2)}
            />
          );
        })}

        {hover && (
          <line
            className="crosshair"
            x1={PAD.l + hover.i * slot + slot / 2}
            x2={PAD.l + hover.i * slot + slot / 2}
            y1={PAD.t}
            y2={PAD.t + PLOT_H}
          />
        )}

        <text className="axis" x={PAD.l} y={H - 4}>{points[0]!.label}</text>
        <text className="axis" x={W - PAD.r} y={H - 4} textAnchor="end">
          {points[points.length - 1]!.label}
        </text>
      </svg>

      {hover && <Tooltip left={hover.x}>{points[hover.i]!.hint}</Tooltip>}
    </div>
  );
}
